import {clsx} from 'clsx';
import type {CSSProperties} from 'react';
import {memo, useMemo} from 'react';

import type {SizeString} from '#theme';
import {Color, colorVarMap, Size, sizeVarMap} from '#theme';
import type {CSSVars} from '#types/util.js';
import {semanticVars as designSystemVars} from '#ui/design_system/contract.css.js';

import {Icons} from './icons.js';
import * as styles from './style.module.scss';

type DesignSystemColor = keyof typeof designSystemVars.color;
type IconColor = Color | DesignSystemColor;

const isThemeColor = (value: string): value is Color =>
  Object.prototype.hasOwnProperty.call(colorVarMap, value);

const isDesignSystemColor = (value: string): value is DesignSystemColor =>
  Object.prototype.hasOwnProperty.call(designSystemVars.color, value);

export type IconProps = {
  name: Icons;
  size?: Size;
  /**
   * Supports legacy theme colors (from `theme/color.ts`) and
   * design-system semantic colors (from `ui/design_system/contract.css.ts`).
   */
  color?: IconColor;
  style?: CSSVars<{size?: SizeString; color?: string}>;
  className?: string;
};

export const Icon = memo(
  ({
    name,
    style,
    className,
    color = Color.Body,
    size = Size.Twelve,
  }: IconProps) => {
    const sizeValue = sizeVarMap[size];
    const iconDef = Icons[name];
    const Comp = iconDef.component;
    const classes = clsx(styles.icon, styles[iconDef.provider], className);
    const resolvedColor = useMemo(() => {
      if (isThemeColor(color)) return colorVarMap[color];
      if (isDesignSystemColor(color)) return designSystemVars.color[color];
      return colorVarMap[Color.Body];
    }, [color]);

    return (
      <Comp
        className={classes}
        style={
          {
            '--size': style?.['--size'] ?? sizeValue,
            '--color': style?.['--color'] ?? 'unset',
            color: `var(--color, ${resolvedColor})`,
          } as CSSProperties
        }
      />
    );
  }
);
Icon.displayName = 'Icon';
